import {
  HEAT_SOURCES,
  HOT_WATER_SOURCES,
  OCCUPANCY_STATUSES,
  STOVE_FUELS,
  UNIT_TYPES,
  type UpdateUnitInput,
} from '@sitewise/shared';
import {
  Button,
  Input,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
} from '@sitewise/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';

import { updateUnit, type UnitRow } from '../../../api/buildings.js';
import { ApiError } from '../../../lib/api-client.js';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unit: UnitRow;
}

interface FormState {
  floor: string;
  unitType: string;
  occupancyStatus: string;
  bedroomCount: string;
  bathroomCount: string;
  squareFeet: string;
  notes: string;
  // utilities
  gasService: '' | 'true' | 'false';
  electricService: '' | 'true' | 'false';
  heatSource: string;
  hotWaterSource: string;
  // appliance summary
  stoveFuel: string;
  stoveLastReplaced: string;
  refrigeratorLastReplaced: string;
  dishwasherPresent: '' | 'true' | 'false';
  dishwasherLastReplaced: string;
  washerDryerPresent: '' | 'true' | 'false';
  washerDryerLastReplaced: string;
  windowAcCount: string;
  centralAc: '' | 'true' | 'false';
}

const NULLABLE_BOOL_OPTIONS: Array<{ value: '' | 'true' | 'false'; label: string }> = [
  { value: '', label: 'Unknown' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

function fromUnit(u: UnitRow): FormState {
  return {
    floor: u.floor ?? '',
    unitType: u.unitType ?? '',
    occupancyStatus: u.occupancyStatus,
    bedroomCount: u.bedroomCount != null ? String(u.bedroomCount) : '',
    bathroomCount: u.bathroomCount != null ? String(u.bathroomCount) : '',
    squareFeet: u.squareFeet != null ? String(u.squareFeet) : '',
    notes: u.notes ?? '',
    gasService: boolToOption(u.gasService),
    electricService: boolToOption(u.electricService),
    heatSource: u.heatSource ?? '',
    hotWaterSource: u.hotWaterSource ?? '',
    stoveFuel: u.stoveFuel ?? '',
    stoveLastReplaced: u.stoveLastReplaced ?? '',
    refrigeratorLastReplaced: u.refrigeratorLastReplaced ?? '',
    dishwasherPresent: boolToOption(u.dishwasherPresent),
    dishwasherLastReplaced: u.dishwasherLastReplaced ?? '',
    washerDryerPresent: boolToOption(u.washerDryerPresent),
    washerDryerLastReplaced: u.washerDryerLastReplaced ?? '',
    windowAcCount: u.windowAcCount != null ? String(u.windowAcCount) : '',
    centralAc: boolToOption(u.centralAc),
  };
}

function boolToOption(b: boolean | null): '' | 'true' | 'false' {
  if (b === null) return '';
  return b ? 'true' : 'false';
}

function optionToBool(o: '' | 'true' | 'false'): boolean | null {
  if (o === '') return null;
  return o === 'true';
}

function strOrNull(v: string): string | null {
  const trimmed = v.trim();
  return trimmed === '' ? null : trimmed;
}

function intOrNull(v: string): number | null {
  if (v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

function numOrNull(v: string): number | null {
  if (v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Slide-over editor for the unit's standard fields. Custom fields are
 * edited inline on the Overview tab, so this sheet only carries the
 * column-backed values. Submits a single PATCH that the API audits as
 * one `unit.updated` entry — the audit `metadata.fields` list reveals
 * which columns actually changed for the activity feed.
 */
export function EditUnitSheet({ open, onOpenChange, unit }: Props) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(() => fromUnit(unit));
  const [error, setError] = useState<string | null>(null);

  // Reset the draft when the sheet is reopened against a freshly
  // fetched unit row.
  useEffect(() => {
    if (open) {
      setForm(fromUnit(unit));
      setError(null);
    }
  }, [open, unit]);

  const mutation = useMutation({
    mutationFn: () => {
      const patch: UpdateUnitInput = {
        floor: strOrNull(form.floor),
        unitType: form.unitType === '' ? null : (form.unitType as UpdateUnitInput['unitType']),
        occupancyStatus: form.occupancyStatus as UpdateUnitInput['occupancyStatus'],
        bedroomCount: intOrNull(form.bedroomCount),
        bathroomCount: numOrNull(form.bathroomCount),
        squareFeet: intOrNull(form.squareFeet),
        notes: strOrNull(form.notes),
        gasService: optionToBool(form.gasService),
        electricService: optionToBool(form.electricService),
        heatSource:
          form.heatSource === '' ? null : (form.heatSource as UpdateUnitInput['heatSource']),
        hotWaterSource:
          form.hotWaterSource === ''
            ? null
            : (form.hotWaterSource as UpdateUnitInput['hotWaterSource']),
        stoveFuel:
          form.stoveFuel === '' ? null : (form.stoveFuel as UpdateUnitInput['stoveFuel']),
        stoveLastReplaced: strOrNull(form.stoveLastReplaced),
        refrigeratorLastReplaced: strOrNull(form.refrigeratorLastReplaced),
        dishwasherPresent: optionToBool(form.dishwasherPresent),
        dishwasherLastReplaced: strOrNull(form.dishwasherLastReplaced),
        washerDryerPresent: optionToBool(form.washerDryerPresent),
        washerDryerLastReplaced: strOrNull(form.washerDryerLastReplaced),
        windowAcCount: intOrNull(form.windowAcCount),
        centralAc: optionToBool(form.centralAc),
      };
      return updateUnit(unit.id, patch);
    },
    onSuccess: () => {
      toast.success('Unit updated.');
      void queryClient.invalidateQueries({ queryKey: ['unit', unit.id] });
      void queryClient.invalidateQueries({ queryKey: ['unit', unit.id, 'activity'] });
      void queryClient.invalidateQueries({
        queryKey: ['building', unit.buildingId, 'units'],
      });
      onOpenChange(false);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Could not update unit');
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Edit unit {unit.unitNumber}</SheetTitle>
          <SheetDescription>
            Update the physical attributes of this unit. Use Custom fields on the Overview tab
            for org-defined data.
          </SheetDescription>
        </SheetHeader>

        <form className="flex flex-1 flex-col gap-4 overflow-y-auto pr-1" onSubmit={handleSubmit}>
          <Section title="Basics">
            <Field label="Floor">
              <Input
                value={form.floor}
                onChange={(e) => setForm((s) => ({ ...s, floor: e.target.value }))}
                placeholder="4"
              />
            </Field>
            <Field label="Unit type">
              <Select
                value={form.unitType}
                options={[
                  { value: '', label: '— None —' },
                  ...UNIT_TYPES.map((t) => ({ value: t, label: t.replace('_', ' ') })),
                ]}
                onChange={(v) => setForm((s) => ({ ...s, unitType: v }))}
              />
            </Field>
            <Field label="Occupancy">
              <Select
                value={form.occupancyStatus}
                options={OCCUPANCY_STATUSES.map((t) => ({
                  value: t,
                  label: t.replace('_', ' '),
                }))}
                onChange={(v) => setForm((s) => ({ ...s, occupancyStatus: v }))}
              />
            </Field>
            <Field label="Bedrooms">
              <Input
                type="number"
                min={0}
                value={form.bedroomCount}
                onChange={(e) => setForm((s) => ({ ...s, bedroomCount: e.target.value }))}
              />
            </Field>
            <Field label="Bathrooms">
              <Input
                type="number"
                min={0}
                step={0.5}
                value={form.bathroomCount}
                onChange={(e) => setForm((s) => ({ ...s, bathroomCount: e.target.value }))}
              />
            </Field>
            <Field label="Square feet">
              <Input
                type="number"
                min={0}
                value={form.squareFeet}
                onChange={(e) => setForm((s) => ({ ...s, squareFeet: e.target.value }))}
              />
            </Field>
            <Field label="Notes" full>
              <textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
                className="rounded border border-border bg-surface px-3 py-2 text-sm"
              />
            </Field>
          </Section>

          <Section title="Utilities">
            <Field label="Gas service">
              <Select
                value={form.gasService}
                options={NULLABLE_BOOL_OPTIONS}
                onChange={(v) =>
                  setForm((s) => ({ ...s, gasService: v as FormState['gasService'] }))
                }
              />
            </Field>
            <Field label="Electric service">
              <Select
                value={form.electricService}
                options={NULLABLE_BOOL_OPTIONS}
                onChange={(v) =>
                  setForm((s) => ({ ...s, electricService: v as FormState['electricService'] }))
                }
              />
            </Field>
            <Field label="Heat source">
              <Select
                value={form.heatSource}
                options={[
                  { value: '', label: '— Unknown —' },
                  ...HEAT_SOURCES.map((t) => ({ value: t, label: t.replace(/_/g, ' ') })),
                ]}
                onChange={(v) => setForm((s) => ({ ...s, heatSource: v }))}
              />
            </Field>
            <Field label="Hot water">
              <Select
                value={form.hotWaterSource}
                options={[
                  { value: '', label: '— Unknown —' },
                  ...HOT_WATER_SOURCES.map((t) => ({ value: t, label: t.replace(/_/g, ' ') })),
                ]}
                onChange={(v) => setForm((s) => ({ ...s, hotWaterSource: v }))}
              />
            </Field>
          </Section>

          <Section title="Appliances">
            <Field label="Stove fuel">
              <Select
                value={form.stoveFuel}
                options={[
                  { value: '', label: '— Unknown —' },
                  ...STOVE_FUELS.map((t) => ({ value: t, label: t.replace(/_/g, ' ') })),
                ]}
                onChange={(v) => setForm((s) => ({ ...s, stoveFuel: v }))}
              />
            </Field>
            <Field label="Stove replaced">
              <Input
                type="date"
                value={form.stoveLastReplaced}
                onChange={(e) => setForm((s) => ({ ...s, stoveLastReplaced: e.target.value }))}
              />
            </Field>
            <Field label="Refrigerator replaced">
              <Input
                type="date"
                value={form.refrigeratorLastReplaced}
                onChange={(e) =>
                  setForm((s) => ({ ...s, refrigeratorLastReplaced: e.target.value }))
                }
              />
            </Field>
            <Field label="Dishwasher present">
              <Select
                value={form.dishwasherPresent}
                options={NULLABLE_BOOL_OPTIONS}
                onChange={(v) =>
                  setForm((s) => ({
                    ...s,
                    dishwasherPresent: v as FormState['dishwasherPresent'],
                  }))
                }
              />
            </Field>
            <Field label="Dishwasher replaced">
              <Input
                type="date"
                value={form.dishwasherLastReplaced}
                onChange={(e) =>
                  setForm((s) => ({ ...s, dishwasherLastReplaced: e.target.value }))
                }
              />
            </Field>
            <Field label="Washer/dryer present">
              <Select
                value={form.washerDryerPresent}
                options={NULLABLE_BOOL_OPTIONS}
                onChange={(v) =>
                  setForm((s) => ({
                    ...s,
                    washerDryerPresent: v as FormState['washerDryerPresent'],
                  }))
                }
              />
            </Field>
            <Field label="Washer/dryer replaced">
              <Input
                type="date"
                value={form.washerDryerLastReplaced}
                onChange={(e) =>
                  setForm((s) => ({ ...s, washerDryerLastReplaced: e.target.value }))
                }
              />
            </Field>
            <Field label="Window AC count">
              <Input
                type="number"
                min={0}
                value={form.windowAcCount}
                onChange={(e) => setForm((s) => ({ ...s, windowAcCount: e.target.value }))}
              />
            </Field>
            <Field label="Central AC">
              <Select
                value={form.centralAc}
                options={NULLABLE_BOOL_OPTIONS}
                onChange={(v) =>
                  setForm((s) => ({ ...s, centralAc: v as FormState['centralAc'] }))
                }
              />
            </Field>
          </Section>

          {error ? <p className="text-sm text-danger">{error}</p> : null}

          <SheetFooter className="mt-auto">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="grid grid-cols-2 gap-3">
      <legend className="col-span-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

function Field({
  label,
  children,
  full,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <div className={`space-y-1 ${full ? 'col-span-2' : ''}`}>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function Select({
  value,
  options,
  onChange,
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded border border-border bg-surface px-3 py-2 text-sm capitalize"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
